import React, { useState, useEffect } from 'react';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Calendar,
  Search,
  BarChart3,
  ArrowUpDown,
  X
} from 'lucide-react';
import api from '../../services/api';

const SMPPAuditDashboard = () => {
  const [auditData, setAuditData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [detailData, setDetailData] = useState(null);
  const [detailConnector, setDetailConnector] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchField, setSearchField] = useState('message_id');
  const [searchValue, setSearchValue] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  
  // Mismatch states
  const [mismatchData, setMismatchData] = useState(null);
  const [mismatchConnector, setMismatchConnector] = useState(null);
  const [mismatchLoading, setMismatchLoading] = useState(false);
  const [mismatchSearchField, setMismatchSearchField] = useState('message_id');
  const [mismatchSearchValue, setMismatchSearchValue] = useState('');
  const [filteredMismatchData, setFilteredMismatchData] = useState([]);

  useEffect(() => {
    fetchAuditData();
  }, [date]);

  // Filter detail data based on search criteria
  useEffect(() => {
    if (!detailData?.data) {
      setFilteredData([]);
      return;
    }

    if (!searchValue.trim()) {
      setFilteredData(detailData.data);
      return;
    }

    const filtered = detailData.data.filter(record => {
      const value = searchValue.toLowerCase();
      
      switch (searchField) {
        case 'message_id':
          return record.message_id?.toLowerCase().includes(value);
        case 'operator_message_id':
          return record.operator_message_id?.toLowerCase().includes(value);
        case 'mobile_number':
          return record.mobile_number?.toLowerCase().includes(value);
        case 'status':
          return record.status?.toLowerCase().includes(value);
        default:
          return false;
      }
    });

    setFilteredData(filtered);
  }, [detailData, searchField, searchValue]);

  // Filter mismatch data based on search criteria
  useEffect(() => {
    if (!mismatchData?.data) {
      setFilteredMismatchData([]);
      return;
    }

    if (!mismatchSearchValue.trim()) {
      setFilteredMismatchData(mismatchData.data);
      return;
    }

    const filtered = mismatchData.data.filter(record => {
      const value = mismatchSearchValue.toLowerCase();
      
      switch (mismatchSearchField) {
        case 'message_id':
          return record.message_id?.toLowerCase().includes(value);
        case 'mobile_number':
          return record.mobile_number?.toLowerCase().includes(value);
        case 'user_id':
          return record.user_id?.toLowerCase().includes(value);
        case 'dlr_status':
          return record.dlr_status?.toLowerCase().includes(value);
        default:
          return false;
      }
    });

    setFilteredMismatchData(filtered);
  }, [mismatchData, mismatchSearchField, mismatchSearchValue]);

  const fetchAuditData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/smpp/audit/dashboard?date=${date}`);
      if (response.data.success) {
        setAuditData(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch audit data:', error);
    }
    setLoading(false);
  };

  const fetchDetail = async (connectorId, connectorName) => {
    setDetailLoading(true);
    setDetailConnector(connectorName);
    setSearchValue(''); // Reset search when loading new detail
    setSearchField('message_id'); // Reset to default field
    try {
      const response = await api.get(`/admin/smpp/audit/detail?connector_id=${connectorId}&date=${date}`);
      if (response.data.success) {
        setDetailData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch detail:', error);
    }
    setDetailLoading(false);
  };

  const fetchMismatch = async (connectorId, connectorName) => {
    setMismatchLoading(true);
    setMismatchConnector(connectorName);
    setMismatchSearchValue(''); // Reset search when loading new mismatch
    setMismatchSearchField('message_id'); // Reset to default field
    try {
      const response = await api.get(`/admin/smpp/audit/mismatch?connector_id=${connectorId}&date=${date}`);
      if (response.data.success) {
        setMismatchData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch mismatch:', error);
    }
    setMismatchLoading(false);
  };

  const totals = auditData.reduce((acc, d) => ({
    submitted: acc.submitted + d.submitted_count,
    respStored: acc.respStored + d.submit_resp_count,
    success: acc.success + d.success_count,
    failed: acc.failed + d.failed_count,
    orphan: acc.orphan + d.orphan_count,
    dlr: acc.dlr + d.dlr_received,
    smsReports: acc.smsReports + d.sms_report_count,
    mismatch: acc.mismatch + d.mismatch_count,
  }), { submitted: 0, respStored: 0, success: 0, failed: 0, orphan: 0, dlr: 0, smsReports: 0, mismatch: 0 });

  return (
    <div style={{ padding: '24px', background: '#0f172a', minHeight: '100vh', color: '#e2e8f0' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Shield size={28} color="#60a5fa" />
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>SMPP Audit Dashboard</h1>
            <p style={{ fontSize: '14px', color: '#94a3b8', margin: 0 }}>Reconciliation: Submit vs Response vs DLR</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#1e293b', padding: '8px 16px', borderRadius: '8px', border: '1px solid #334155' }}>
            <Calendar size={16} color="#94a3b8" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ background: 'transparent', border: 'none', color: '#e2e8f0', fontSize: '14px', outline: 'none' }}
            />
          </div>
          <button
            onClick={fetchAuditData}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#3b82f6', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Submit Resp Stored', value: totals.respStored, icon: <BarChart3 size={20} />, color: '#3b82f6' },
          { label: 'Success', value: totals.success, icon: <CheckCircle size={20} />, color: '#22c55e' },
          { label: 'Failed', value: totals.failed, icon: <XCircle size={20} />, color: '#ef4444' },
          { label: 'Orphaned', value: totals.orphan, icon: <AlertTriangle size={20} />, color: '#f59e0b' },
        ].map((card, i) => (
          <div key={i} style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', border: '1px solid #334155' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', color: '#94a3b8' }}>{card.label}</span>
              <span style={{ color: card.color }}>{card.icon}</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: card.color }}>{card.value.toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* Second row of cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'SMS Reports', value: totals.smsReports, color: '#8b5cf6' },
          { label: 'DLR Received', value: totals.dlr, color: '#06b6d4' },
          { label: 'Submitted (operator_submitted=1)', value: totals.submitted, color: '#10b981' },
          { label: 'Mismatch Count', value: totals.mismatch, color: totals.mismatch > 0 ? '#ef4444' : '#22c55e' },
        ].map((card, i) => (
          <div key={i} style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', border: '1px solid #334155' }}>
            <span style={{ fontSize: '13px', color: '#94a3b8' }}>{card.label}</span>
            <div style={{ fontSize: '28px', fontWeight: 700, color: card.color, marginTop: '8px' }}>{card.value.toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* Per-Connector Table */}
      <div style={{ background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden', marginBottom: '24px' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ArrowUpDown size={16} color="#60a5fa" />
          <span style={{ fontWeight: 600 }}>Per-Connector Reconciliation</span>
        </div>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
        ) : auditData.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No data for {date}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#0f172a' }}>
                {['Connector', 'System ID', 'Submitted', 'Resp Stored', 'Success', 'Failed', 'Orphan', 'DLR', 'SMS Reports', 'Mismatch', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, borderBottom: '1px solid #334155' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {auditData.map((d, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600 }}>{d.connector_name}</td>
                  <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{d.system_id}</td>
                  <td style={{ padding: '10px 12px' }}>{d.submitted_count}</td>
                  <td style={{ padding: '10px 12px' }}>{d.submit_resp_count}</td>
                  <td style={{ padding: '10px 12px', color: '#22c55e' }}>{d.success_count}</td>
                  <td style={{ padding: '10px 12px', color: '#ef4444' }}>{d.failed_count}</td>
                  <td style={{ padding: '10px 12px', color: d.orphan_count > 0 ? '#f59e0b' : '#94a3b8' }}>{d.orphan_count}</td>
                  <td style={{ padding: '10px 12px', color: '#06b6d4' }}>{d.dlr_received}</td>
                  <td style={{ padding: '10px 12px' }}>{d.sms_report_count}</td>
                  <td style={{ padding: '10px 12px', color: d.mismatch_count > 0 ? '#ef4444' : '#22c55e', fontWeight: 600 }}>
                    {d.mismatch_count > 0 ? `⚠ ${d.mismatch_count}` : '✓ 0'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => fetchDetail(d.connector_id, d.connector_name)}
                        style={{ background: '#334155', color: '#60a5fa', border: 'none', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                      >
                        <Search size={12} /> Detail
                      </button>
                      {d.mismatch_count > 0 && (
                        <button
                          onClick={() => fetchMismatch(d.connector_id, d.connector_name)}
                          style={{ background: '#dc2626', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <AlertTriangle size={12} /> Mismatch ({d.mismatch_count})
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Panel */}
      {detailData && (
        <div style={{ background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600 }}>
              Detail: {detailConnector} — {detailData.total} records on {detailData.date}
            </span>
            <button
              onClick={() => { setDetailData(null); setDetailConnector(null); setSearchValue(''); }}
              style={{ background: '#334155', color: '#94a3b8', border: 'none', padding: '4px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
            >
              Close
            </button>
          </div>
          
          {/* Search Section */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #334155', background: '#0f172a' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '13px', color: '#94a3b8' }}>Search by:</label>
                <select
                  value={searchField}
                  onChange={(e) => setSearchField(e.target.value)}
                  style={{
                    background: '#1e293b',
                    color: '#e2e8f0',
                    border: '1px solid #334155',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                >
                  <option value="message_id">Message ID</option>
                  <option value="operator_message_id">Operator Message ID</option>
                  <option value="mobile_number">Mobile</option>
                  <option value="status">Status</option>
                </select>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '200px' }}>
                <input
                  type="text"
                  placeholder="Enter search value..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  style={{
                    flex: 1,
                    background: '#1e293b',
                    color: '#e2e8f0',
                    border: '1px solid #334155',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
              </div>
              
              <button
                onClick={() => setSearchValue('')}
                style={{
                  background: '#334155',
                  color: '#94a3b8',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <X size={14} /> Reset
              </button>
            </div>
            
            {searchValue && (
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#60a5fa' }}>
                Found {filteredData.length} matching records
              </div>
            )}
          </div>
          
          {detailLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#0f172a', position: 'sticky', top: 0 }}>
                    {['ID', 'Message ID', 'Operator Msg ID', 'Status', 'Error', 'Mobile', 'User', 'Time'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, borderBottom: '1px solid #334155' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 ? (
                    <tr>
                      <td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                        {searchValue ? 'No records match your search criteria' : 'No records found'}
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #1e293b' }}>
                        <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{r.id}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '11px', color: r.message_id?.startsWith('ORPHAN') ? '#f59e0b' : '#e2e8f0' }}>{r.message_id}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '11px' }}>{r.operator_message_id || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                            background: r.status === 'SUCCESS' ? '#052e16' : '#450a0a',
                            color: r.status === 'SUCCESS' ? '#22c55e' : '#ef4444'
                          }}>
                            {r.status}
                          </span>
                        </td>
                        <td style={{ padding: '6px 10px', color: '#94a3b8', fontSize: '11px' }}>{r.error_description || '—'}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '11px' }}>{r.mobile_number}</td>
                        <td style={{ padding: '6px 10px' }}>{r.user_id}</td>
                        <td style={{ padding: '6px 10px', color: '#94a3b8', fontSize: '11px' }}>{r.created_at}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Mismatch Panel */}
      {mismatchData && (
        <div style={{ background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden', marginTop: '24px' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, color: '#dc2626' }}>
              Mismatch Records: {mismatchConnector} — {mismatchData.total} records on {mismatchData.date}
            </span>
            <button
              onClick={() => { setMismatchData(null); setMismatchConnector(null); setMismatchSearchValue(''); }}
              style={{ background: '#334155', color: '#94a3b8', border: 'none', padding: '4px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
            >
              Close
            </button>
          </div>
          
          {/* Search Section */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #334155', background: '#0f172a' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '13px', color: '#94a3b8' }}>Search by:</label>
                <select
                  value={mismatchSearchField}
                  onChange={(e) => setMismatchSearchField(e.target.value)}
                  style={{
                    background: '#1e293b',
                    color: '#e2e8f0',
                    border: '1px solid #334155',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                >
                  <option value="message_id">Message ID</option>
                  <option value="mobile_number">Mobile</option>
                  <option value="user_id">User ID</option>
                  <option value="dlr_status">DLR Status</option>
                </select>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '200px' }}>
                <input
                  type="text"
                  placeholder="Enter search value..."
                  value={mismatchSearchValue}
                  onChange={(e) => setMismatchSearchValue(e.target.value)}
                  style={{
                    flex: 1,
                    background: '#1e293b',
                    color: '#e2e8f0',
                    border: '1px solid #334155',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
              </div>
              
              <button
                onClick={() => setMismatchSearchValue('')}
                style={{
                  background: '#334155',
                  color: '#94a3b8',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <X size={14} /> Reset
              </button>
            </div>
            
            {mismatchSearchValue && (
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#60a5fa' }}>
                Found {filteredMismatchData.length} matching records
              </div>
            )}
          </div>
          
          {mismatchLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#0f172a', position: 'sticky', top: 0 }}>
                    {['ID', 'Message ID', 'Mobile', 'User ID', 'DLR Status', 'Error Code', 'Time'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, borderBottom: '1px solid #334155' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredMismatchData.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                        {mismatchSearchValue ? 'No records match your search criteria' : 'No mismatch records found'}
                      </td>
                    </tr>
                  ) : (
                    filteredMismatchData.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #1e293b', backgroundColor: 'rgba(220, 38, 38, 0.05)' }}>
                        <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{r.id}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '11px', color: '#e2e8f0' }}>{r.message_id}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '11px' }}>{r.mobile_number}</td>
                        <td style={{ padding: '6px 10px' }}>{r.user_id}</td>
                        <td style={{ padding: '6px 10px' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                            background: '#450a0a',
                            color: '#ef4444'
                          }}>
                            {r.dlr_status || 'UNKNOWN'}
                          </span>
                        </td>
                        <td style={{ padding: '6px 10px', color: '#94a3b8', fontSize: '11px' }}>{r.error_code || '—'}</td>
                        <td style={{ padding: '6px 10px', color: '#94a3b8', fontSize: '11px' }}>{r.created_at}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SMPPAuditDashboard;
